import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ArrowRight, ArrowLeft, MapPin } from 'lucide-react';
import { sowService } from '../../services/sowService';
import { CustomerInfo } from '../../models/sow';
import propertyService from '../../services/propertyService';
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';
import AccessTokenManager from '../../services/accessTokenManager';

type CreateStage = 'address' | 'creating';

interface CreateSOWDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSOWCreated: (sowId: string) => void;
}

export const CreateSOWDialog: React.FC<CreateSOWDialogProps> = ({
  isOpen,
  onClose,
  onSOWCreated,
}) => {
  const [stage, setStage] = useState<CreateStage>('address');
  const [selectedAddress, setSelectedAddress] = useState('');
  const [addressComponents, setAddressComponents] = useState<any>(null);
  
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete();

  const resetForm = () => {
    setStage('address');
    setSelectedAddress('');
    setAddressComponents(null);
    setValue('');
    clearSuggestions();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // No multi-step flow anymore, just address then create

  const handleAddressSelected = async (address: string) => {
    setValue(address, false);
    clearSuggestions();
    setSelectedAddress(address);
    
    // Try to get more detailed address information using geocoding
    try {
      const results = await getGeocode({ address });
      if (results.length > 0) {
        const place = results[0];
        console.log('Geocoded place details:', place);
        
        // Extract and store components for better parsing
        const components = place.address_components;
        console.log('Address components:', components);
        setAddressComponents(components);
      }
    } catch (error) {
      console.log('Geocoding failed:', error);
      // Continue with basic parsing if geocoding fails
      setAddressComponents(null);
    }
  };

  const handleCreateSOW = async () => {
    try {
      setStage('creating');

      console.log('Creating SOW with address:', selectedAddress);
      
      // Check if user is authenticated
      const token = AccessTokenManager.getAccessToken();
      console.log('Auth token available:', !!token);
      if (!token) {
        throw new Error('User not authenticated');
      }

      // Create property first using the selected address
      let streetAddress = '';
      let city = '';
      let state = '';
      let zip = '';
      
      // Use geocoded address components if available
      if (addressComponents) {
        console.log('Using geocoded address components:', addressComponents);
        
        const streetNumber = addressComponents.find((c: any) => c.types.includes('street_number'))?.long_name || '';
        const streetName = addressComponents.find((c: any) => c.types.includes('route'))?.long_name || '';
        streetAddress = `${streetNumber} ${streetName}`.trim();
        
        city = addressComponents.find((c: any) => c.types.includes('locality'))?.long_name || '';
        state = addressComponents.find((c: any) => c.types.includes('administrative_area_level_1'))?.short_name || '';
        zip = addressComponents.find((c: any) => c.types.includes('postal_code'))?.long_name || '';
      } else {
        // Fallback to basic parsing
        console.log('Using basic address parsing');
        const addressParts = selectedAddress.split(', ');
        console.log('Address parts:', addressParts);
        
        streetAddress = addressParts[0] || '';
        city = addressParts[1] || '';
        
        if (addressParts.length >= 3) {
          const stateZipPart = addressParts[2];
          // Check if it contains both state and zip (e.g., "MI 48178" or just "MI")
          const stateZipMatch = stateZipPart.match(/^([A-Z]{2})\s*(\d{5}(?:-\d{4})?)?/);
          if (stateZipMatch) {
            state = stateZipMatch[1];
            zip = stateZipMatch[2] || '';
          } else {
            state = stateZipPart;
          }
        }
      }

      console.log('Creating property with:', { streetAddress, city, state, zip, fullAddress: selectedAddress });

      const propertyId = await propertyService.createProperty(
        streetAddress, 
        city, 
        state || '', 
        zip || ''
      );

      console.log('Property created with ID:', propertyId);

      if (!propertyId) {
        throw new Error('Failed to create property');
      }

      // Create SOW with address - no customer info needed, user is creating their own
      console.log('Creating SOW with address:', selectedAddress);
      const newSOW = await sowService.createSOW(selectedAddress);
      console.log('SOW created:', newSOW);
      
      // Update SOW with propertyId
      console.log('Updating SOW with propertyId:', propertyId);
      await sowService.updateSOW(newSOW.id, { propertyId });
      console.log('SOW updated with propertyId');

      onSOWCreated(newSOW.id);
      handleClose();
    } catch (error) {
      console.error('Error creating SOW:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to create SOW: ${errorMessage}`);
      setStage('address'); // Go back to address step on error
    }
  };

  const canCreate = selectedAddress.trim() !== '';

  const renderStage = () => {
    if (stage === 'address') {
      return (
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label className="text-sm font-medium">
              Property Address *
            </label>
            <p className="text-sm text-gray-600">
              Enter the address where the renovation work will take place.
            </p>
            
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Start typing an address..."
                value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={!ready}
                className="pl-10"
              />
            </div>
            
            {/* Address Suggestions */}
            {status === 'OK' && (
              <div className="border rounded-md shadow-sm overflow-hidden">
                {data.map((suggestion: any) => (
                  <div
                    key={suggestion.place_id}
                    onClick={() => handleAddressSelected(suggestion.description)}
                    className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  >
                    <div className="flex items-start">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm">{suggestion.description}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {selectedAddress && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-800">Selected: {selectedAddress}</p>
            </div>
          )}
        </div>
      );
    }

    if (stage === 'creating') {
      return (
        <div className="grid gap-4 py-4 text-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="text-lg font-medium">Creating Project...</div>
            <div className="text-sm text-gray-600">
              • Creating property record<br/>
              • Setting up customer workflow<br/>
              • Generating shareable link
            </div>
          </div>
        </div>
      );
    }
  };

  const getTitle = () => {
    switch (stage) {
      case 'address':
        return 'Create Project';
      case 'creating':
        return 'Creating Project...';
      default:
        return 'Create New Project';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
        </DialogHeader>
        
        {renderStage()}
        
        {stage !== 'creating' && (
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
            
            <Button
              onClick={handleCreateSOW}
              disabled={!canCreate}
            >
              Create SOW
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};